"use client";

import { SceneKnowledgeBaseDetail } from "@/api";
import PaperclipIcon from "@/assets/icons/paperclip.svg";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { ChatBox } from "@/component/ChatBox";
import { KnowledgeBaseSelector } from "@/component/ChatBox/KnowledgeBaseSelector";
import { SpotlightAnchor } from "@/component/NewUserGuide/SpotlightAnchor";
import { useSpotlight } from "@/component/NewUserGuide/SpotlightContext";
import { SpotlightPopover } from "@/component/NewUserGuide/SpotlightPopover";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { Suspense, useCallback, useState } from "react";
import bilibiliIconUrl from "./assets/bilibili-icon.png";
import sunoIconUrl from "./assets/suno-icon.png";
import xIconUrl from "./assets/x-icon.png";
import xiaohongshuIconUrl from "./assets/xiaohongshu-icon.png";
import youtubeIconUrl from "./assets/youtube-icon.png";

function ChatBoxInner({
  knowledgeBases,
}: {
  knowledgeBases?: SceneKnowledgeBaseDetail[];
}) {
  const searchParams = useSearchParams();
  const initialPrompt = searchParams.get("prompt") || undefined;

  const { activeStep, setStep, completeGuide } = useSpotlight();

  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<
    { id: string; text: string } | undefined
  >();

  const handleKnowledgeBaseChange = useCallback(
    (item: { id: string; text: string }) => {
      setSelectedKnowledgeBase(item);
      logger.event(VidMuseEvent.KnowledgeBaseSelected, {
        knowledge_base_id: item.id,
        knowledge_base_name: item.text,
      });
    },
    [],
  );

  const handleRemoveKnowledgeBase = useCallback(() => {
    setSelectedKnowledgeBase(undefined);
  }, []);

  const totalSteps = 3;

  return (
    <>
      <SpotlightAnchor
        step={1}
        margin={16}
        rounded="36px"
        gradientWidth={130}
        gradientHeight={130}
        popover={
          <SpotlightPopover
            currentStep={1}
            totalSteps={totalSteps}
            onNext={() => setStep(2)}
            onClose={completeGuide}
            videoUrl="https://static.sandcdn.com/uploads/9a6806d5-1277-4f16-a94f-5523aeda72b3.mp4"
            posterUrl="https://static.sandcdn.com/uploads/5750b223-7c53-4bee-a02f-ade645830b56.jpg"
            className="left-0 -top-[319px]"
            content={
              <>
                <p>
                  Paste a link or upload a file to start your visual journey.
                </p>
                <div className="flex items-center text-text-tertiary mt-1">
                  <PaperclipIcon className="w-4 h-4 mr-0.5" />
                  <span className="mr-2 shrink-0">Supported Links:</span>
                  <div className="flex gap-1">
                    <Image
                      src={sunoIconUrl}
                      alt="SunoIcon"
                      width={24}
                      height={24}
                    />
                    <Image
                      src={youtubeIconUrl}
                      alt="YoutubeIcon"
                      width={24}
                      height={24}
                    />
                    <Image src={xIconUrl} alt="XIcon" width={24} height={24} />
                    <Image
                      src={bilibiliIconUrl}
                      alt="BilibiliIcon"
                      width={24}
                      height={24}
                    />
                    <Image
                      src={xiaohongshuIconUrl}
                      alt="XiaohongshuIcon"
                      width={24}
                      height={24}
                    />
                  </div>
                </div>
              </>
            }
          />
        }
      >
        <ChatBox
          initialText={initialPrompt}
          selectedKnowledgeBase={selectedKnowledgeBase}
          onKnowledgeBaseRemove={handleRemoveKnowledgeBase}
        />
      </SpotlightAnchor>

      <div className="mt-8 flex justify-center">
        <SpotlightAnchor
          step={2}
          margin={16}
          rounded="16px"
          gradientWidth={600}
          gradientHeight={70}
          popover={
            <SpotlightPopover
              currentStep={2}
              totalSteps={totalSteps}
              onNext={() => setStep(3)}
              onClose={completeGuide}
              videoUrl="https://static.sandcdn.com/uploads/45c178c3-510e-4e28-9a71-2c2e83e3922c.mp4"
              posterUrl="https://static.sandcdn.com/uploads/02a9f295-8f81-4fd2-9133-53c291670bbc.jpg"
              videoMarkers={[0, 2.3, 4.3, 7]}
              className="left-1/2 -translate-x-1/2 -top-[292px]"
              content={
                <>
                  <p>Pick a mode to guide your creation.</p>
                </>
              }
            />
          }
        >
          <KnowledgeBaseSelector
            onSelect={handleKnowledgeBaseChange}
            initialData={knowledgeBases}
          />
        </SpotlightAnchor>
      </div>
    </>
  );
}

export function ChatBoxContainer({
  knowledgeBases,
}: {
  knowledgeBases?: SceneKnowledgeBaseDetail[];
}) {
  return (
    <div className="px-5 sm:px-11 max-w-[1000px] mx-auto box-content">
      <Suspense fallback={<ChatBox />}>
        <ChatBoxInner knowledgeBases={knowledgeBases} />
      </Suspense>
    </div>
  );
}
